import { datadogLogs } from '@datadog/browser-logs';
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLoggedInUser } from '@services/auth/use-logged-in-user';
import {
  portalBannersObjectSchema,
  REFETCH_INTERVAL,
  RENDER_INTERVAL,
} from './constants';
import styles from './portal-banner.module.scss';

export interface BannerMessage {
  message: string;
  startTime?: Date;
  endTime?: Date;
  beforeLogin?: boolean;
}

export interface PortalBanners {
  admin: BannerMessage[];
  partner: BannerMessage[];
  member: BannerMessage[];
  pharmacy: BannerMessage[] | undefined;
}

export const PortalBanner = () => {
  const { isLoggedIn } = useLoggedInUser();
  const [bannerMessages, setBannerMessages] = useState<BannerMessage[]>([]);
  const [displayedMessages, setDisplayedMessages] = useState<BannerMessage[]>(
    []
  );

  useEffect(() => {
    const fetchBanners = async () => {
      const response = await fetch('/banner.json', { cache: 'no-store' }).catch(
        e => {
          datadogLogs.logger.error(`Pharmacy banner fetch error`, {}, e);
        }
      );

      if (!response) {
        return;
      }

      try {
        const data = await response?.json();

        await portalBannersObjectSchema.validate(data);

        setBannerMessages(
          data?.pharmacy?.map((banner: BannerMessage) => ({
            ...banner,
            startTime: banner.startTime ? new Date(banner.startTime) : null,
            endTime: banner.endTime ? new Date(banner.endTime) : null,
          })) || []
        );
      } catch (error) {
        const body = await response?.text();

        datadogLogs.logger.error(
          `pharmacy banner parse error`,
          { body },
          error as Error
        );
      }
    };

    const bannerInterval = setInterval(fetchBanners, REFETCH_INTERVAL);
    void fetchBanners();

    return () => clearInterval(bannerInterval);
  }, []);

  useEffect(() => {
    const filterBanners = () => {
      const now = new Date();

      setDisplayedMessages(
        bannerMessages.filter(
          message =>
            (!message.startTime || message.startTime < now) &&
            (!message.endTime || message.endTime > now) &&
            (message.beforeLogin || isLoggedIn)
        )
      );
    };

    const renderInterval = setInterval(filterBanners, RENDER_INTERVAL);
    filterBanners();

    return () => clearInterval(renderInterval);
  }, [bannerMessages, isLoggedIn]);

  if (displayedMessages.length) {
    return (
      <div className={styles.banner}>
        {displayedMessages.map(banner => (
          <div
            className={styles.bannerLine}
            key={`pharmacy-banner-${banner.message}`}
          >
            <ReactMarkdown
              allowedElements={[
                'p',
                'strong',
                'em',
                'a',
                'br',
                'ul',
                'ol',
                'li',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
              ]}
            >
              {banner.message}
            </ReactMarkdown>
          </div>
        ))}
      </div>
    );
  }
};
